import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { NewsletterForm } from './NewsletterForm';

const GOOGLE_PLAY_BADGES: Record<string, string> = {
  fr: 'GetItOnGooglePlay_Badge_Web_color_French.svg',
  en: 'GetItOnGooglePlay_Badge_Web_color_English.svg',
  es: 'GetItOnGooglePlay_Badge_Web_color_Spanish.svg',
  de: 'GetItOnGooglePlay_Badge_Web_color_German.svg',
  it: 'GetItOnGooglePlay_Badge_Web_color_Italian.svg',
  zh: 'GetItOnGooglePlay_Badge_Web_color_Chinese-China.svg',
};

const GOOGLE_PLAY_FOOTER_URL = 'https://play.google.com/store/apps/details?id=fr.stellarskyimager.planner&utm_source=website&utm_medium=badge&utm_campaign=footer';
const PROMO_START = new Date('2026-05-14T00:00:00');
const PROMO_END = new Date('2026-05-28T00:00:00');

export const Footer: React.FC = () => {
  const { t, i18n } = useTranslation();
  const langCode = i18n.language.slice(0, 2);
  const isPromo = new Date() >= PROMO_START && new Date() < PROMO_END;
  const isPromoComing = new Date() < PROMO_START;
  const badgeFile = GOOGLE_PLAY_BADGES[langCode] ?? GOOGLE_PLAY_BADGES['en'];
  const badgeSrc = `/images/Google%20Play%20Store/${badgeFile}`;

  return (
    <footer className="border-t border-white/10 bg-[#0f2023] py-10 text-white/70 text-sm">
      <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-12 text-left mb-8">
        <div>
          <p className="text-white font-bold mb-4 uppercase tracking-tighter text-xs">{t('footer.menu')}</p>
          <ul className="space-y-2">
            <li><Link to="/" className="hover:text-primary transition-colors">{t('footer.home')}</Link></li>
            <li><Link to="/planner" className="hover:text-primary transition-colors">{t('footer.planner')}</Link></li>
            <li><Link to="/galerie" className="hover:text-primary transition-colors">{t('footer.gallery')}</Link></li>
            <li><Link to="/videos" className="hover:text-primary transition-colors">{t('footer.videos')}</Link></li>
            <li><Link to="/evenements" className="hover:text-primary transition-colors">{t('footer.events')}</Link></li>
            {/* [US 6.16] Page témoignages dédiée */}
            <li><Link to="/temoignages" className="hover:text-primary transition-colors">{t('footer.testimonials')}</Link></li>
          </ul>
        </div>
        <div>
          <p className="text-white font-bold mb-4 uppercase tracking-tighter text-xs">{t('footer.seoGuides')}</p>
          <ul className="space-y-2">
            <li><Link to="/guide-planification" className="hover:text-primary transition-colors text-xs">{t('footer.guidePlan')}</Link></li>
            <li><Link to="/guide-fov" className="hover:text-primary transition-colors text-xs">{t('footer.guideFov')}</Link></li>
            <li><Link to="/guide-filtres" className="hover:text-primary transition-colors text-xs">{t('footer.guideFilters')}</Link></li>
          </ul>
        </div>
        <div>
          <p className="text-white font-bold mb-4 uppercase tracking-tighter text-xs">{t('footer.project')}</p>
          <p className="mb-4 font-light text-[10px] leading-relaxed">
            {t('footer.description')}
          </p>
          <div className="flex gap-4">
            <a href="https://youtube.com/@StellarSkyImager" target="_blank" rel="noreferrer" className="hover:text-primary transition-colors text-xs">YouTube</a>
            <Link to="/contact" className="hover:text-primary transition-colors text-xs">{t('footer.contact')}</Link>
          </div>
          <div className="mt-4">
            <div className="flex items-center gap-2 mb-1">
              {isPromo ? (
                <>
                  <span className="font-black text-primary text-sm">{t('home.androidApp.pricePromo')}</span>
                  <span className="text-white/30 line-through text-xs">{t('home.androidApp.priceRegular')}</span>
                </>
              ) : (
                <span className="text-white/60 text-xs">{t('home.androidApp.priceRegular')}</span>
              )}
              <span className="text-white/30 text-[10px]">· {t('home.androidApp.purchaseType')}</span>
            </div>
            {isPromo && (
              <p className="text-amber-400 text-[10px] font-bold mb-1.5">{t('home.androidApp.promoLabel')}</p>
            )}
            {isPromoComing && (
              <p className="text-sky-300/80 text-[10px] font-bold mb-1.5">{t('home.androidApp.promoComingDate')}</p>
            )}
            <a href={GOOGLE_PLAY_FOOTER_URL} target="_blank" rel="noreferrer" className="inline-block">
              <img src={badgeSrc} alt="Get it on Google Play" className="h-9 w-auto opacity-80 hover:opacity-100 transition-opacity" />
            </a>
            <p className="mt-1.5 text-[10px] text-white/30 flex items-center gap-1">
              <span className="material-symbols-outlined" style={{ fontSize: '11px' }}>language</span>
              {t('home.androidApp.webNote')}
            </p>
          </div>
        </div>
        <div id="newsletter" className="md:border-l md:border-white/5 md:pl-8">
          <NewsletterForm />
        </div>
      </div>
      <div className="text-center pt-8 border-t border-white/5 text-white/70 text-[10px]">
        <p>{t('footer.copyright', { year: new Date().getFullYear() })}</p>
        <div className="flex justify-center gap-4 mt-2">
          <Link to="/confidentialite" className="hover:text-primary transition-colors">{t('footer.privacy')}</Link>
          <span className="text-white/20">|</span>
          <Link to="/mentions-legales" className="hover:text-primary transition-colors">{t('footer.legal')}</Link>
          <span className="text-white/20">|</span>
          <Link to="/cgv" className="hover:text-primary transition-colors">{t('footer.cgv')}</Link>
        </div>
      </div>
    </footer>
  );
};
